import { Duration } from '../../DurationSchema';
import { EbpCaseAppointments } from '../EbpCaseAppointmentsSchema';
import {
  DurationScale,
  EbpCasePatientSupportLevel,
  isEbpCasePatientSupportLevel,
} from '../../enums';

// Whose completed appointments count toward a level's sessions
export enum SupportSessionsCountedBy {
  // appointments.completedByProfessionals: nursing sessions are not counted
  professionals = 'professionals',
  // appointments.completed: every session, nursing included
  everyone = 'everyone',
}

// What a support level grants, as data: nothing reads a level by its name.
// Each row is one entitlement template of the Entitlements design (PRM-68,
// one InsurancePlan per template): `window` becomes its coverage window and
// `sessions` its clinic budget, as a count.
export interface SupportLevelDefinition {
  // Support ends this long after the member's first completed appointment,
  // and nurses are reminded to reauthorise two weeks before. null: support
  // does not end.
  window: Duration | null;
  // The estimated discharge date, counted from the same first completed
  // appointment. null: not estimated.
  estimatedDischarge: Duration | null;
  // The sessions granted before more need pre-authorising. null: uncapped.
  sessions: {
    // the cap a case starts with; an approval may raise it
    minimum: number;
    countedBy: SupportSessionsCountedBy;
    // Nurses are asked to review the reauthorisation at every `reviewEvery`
    // counted sessions, once fewer than that remain. null: never.
    reviewEvery: number | null;
  } | null;
}

const months = (value: number): Duration => ({
  value,
  scale: DurationScale.month,
});

export const supportLevelDefinitions: Record<
  EbpCasePatientSupportLevel,
  SupportLevelDefinition
> = {
  [EbpCasePatientSupportLevel.One]: {
    window: months(3),
    estimatedDischarge: months(3),
    sessions: null,
  },
  [EbpCasePatientSupportLevel.Two]: {
    window: months(6),
    estimatedDischarge: months(6),
    sessions: {
      minimum: 5,
      countedBy: SupportSessionsCountedBy.professionals,
      reviewEvery: 4,
    },
  },
  [EbpCasePatientSupportLevel.Three]: {
    window: null,
    estimatedDischarge: months(1),
    sessions: {
      minimum: 2,
      countedBy: SupportSessionsCountedBy.everyone,
      reviewEvery: null,
    },
  },
  // Vitality: each service is pre-authorised when it is needed
  [EbpCasePatientSupportLevel.ClinicalService]: {
    window: null,
    estimatedDischarge: null,
    sessions: null,
  },
};

// The definition of a case's stored level; null for no level ('-' included)
// or a value no longer defined.
export function getSupportLevelDefinition(
  level: string | null | undefined,
): SupportLevelDefinition | null {
  return isEbpCasePatientSupportLevel(level)
    ? supportLevelDefinitions[level]
    : null;
}

// The completed sessions that count toward a level's cap
export function countCompletedSessions(
  appointments: Partial<EbpCaseAppointments> | null | undefined,
  countedBy: SupportSessionsCountedBy,
) {
  switch (countedBy) {
    case SupportSessionsCountedBy.professionals:
      return appointments?.completedByProfessionals ?? 0;
    case SupportSessionsCountedBy.everyone:
      return appointments?.completed ?? 0;
    default:
      throw new Error(`Unknown sessions counting: ${countedBy}`);
  }
}
